<template>
    <app-base>
        <app-layout v-if="viewer !== undefined">
            <slot />
        </app-layout>

        <!-- CPX uses unstyled dialogs, so responsive sizing must come from CSS rather than PrimeVue breakpoints. -->
        <prime-dialog
            v-if="viewer !== undefined && welcomeData !== undefined"
            v-model:visible="showWelcome"
            :header="welcomeData.organization.welcomeTitle"
            modal
            :dismissable-mask="true"
            class="max-h-[calc(100dvh-2rem)] !w-[calc(100vw-2rem)] max-w-xl"
            @show="handleWelcomeShow"
        >
            <div
                class="m-0 leading-relaxed"
                v-html="welcomeData.organization.welcomeText"
            />
            <template #footer>
                <div
                    class="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-end"
                >
                    <prime-button
                        :label="t('profileInviteWelcome.stayInMyAccount')"
                        class="w-full justify-center sm:w-auto"
                        outlined
                        @click="showWelcome = false"
                    />
                    <prime-button
                        class="w-full justify-center sm:w-auto"
                        :label="
                            t('profileInviteWelcome.visitFacility', {
                                facility: viewer.homeFacility.shortName
                            })
                        "
                        @click="handleVisitHomeStorefront"
                    />
                </div>
            </template>
        </prime-dialog>

        <kiosk-profile-inactivity-timeout
            v-if="isKiosk && viewer !== undefined"
        />
    </app-base>
</template>

<script setup lang="ts">
import AppLayout from '~/components/Layouts/ProfileLayout/AppLayout.vue'
import { provideViewer, useViewer } from '~/composables/use-login'
import { useRoute, useRouter } from 'vue-router'
import { useRouteLink } from '~/composables/routing'
import { computed, onBeforeUnmount, ref } from 'vue'
import type { ActiveFacility } from '~/composables/use-active-facility'
import { provideActiveFacility } from '~/composables/use-active-facility'
import AppBase from '~/components/Layouts/AppBase.vue'
import { navigateTo } from '#app/composables/router'
import { useHead } from '@unhead/vue'
import { createLayout } from '~/components/Layouts/ProfileLayout/composables/layout'
import { useShowAlert } from '~/composables/use-show-alert'
import { useLayoutMode } from '#imports'
import { useI18n } from 'vue-i18n'
import KioskProfileInactivityTimeout from '~/components/Layouts/ProfileLayout/KioskProfileInactivityTimeout.vue'
import PrimeDialog from 'primevue/dialog'
import PrimeButton from 'primevue/button'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'

const router = useRouter()
const { orgSignInLink, facilityHomeLink } = useRouteLink()
const { isKiosk, isMobileApp } = useLayoutMode()
const viewer = useViewer()
const showAlert = useShowAlert()
const { t } = useI18n({ useScope: 'global' })

if (isMobileApp.value) {
    if (viewer.value === undefined) {
        showAlert(t('profile.viewerUndefined'), t('profile.mobileApp'))
    }
} else {
    await provideViewer()
    if (viewer.value === undefined) {
        // Redirect to org login page
        await navigateTo(orgSignInLink(router.currentRoute.value.fullPath))
    }
}

if (viewer.value !== undefined) {
    const homeFacility = ref<ActiveFacility>(viewer.value.homeFacility)
    provideActiveFacility(homeFacility)

    const unmount = ref(false)
    const headStyle = computed(() => {
        if (unmount.value) {
            return []
        }

        return homeFacility.value.branding.styleString
    })

    const headLinks = computed(() => {
        return homeFacility.value.organization.branding.headLinks
    })

    useHead({
        style: headStyle,
        link: headLinks
    })

    onBeforeUnmount(() => {
        // Clear styles when leaving the component
        unmount.value = true
    })
}

// Invitation links show the organization's localized welcome after the profile session is loaded.
// Ordinary profile visits do not need to fetch this content, and the dialog waits for its text.
const route = useRoute()
const showWelcome = ref(route.query.action === 'welcome')
const selectedLanguage = useSelectedLanguage()
const query = useRpQuery({ orgLevel: true })
const { data: welcomeData } = useQuery({
    queryKey: ['ProfileInvitationWelcomeQuery', selectedLanguage],
    enabled: computed(() => showWelcome.value && viewer.value !== undefined),
    queryFn: () => {
        return query(
            graphql(/* GraphQL */ `
                query ProfileInvitationWelcomeQuery($language: Language!) {
                    organization {
                        welcomeTitle(language: $language)
                        welcomeText(language: $language)
                    }
                }
            `),
            { language: selectedLanguage.value }
        )
    }
})

async function handleVisitHomeStorefront() {
    if (viewer.value === undefined) {
        return
    }

    // Use the signed-in profile's home facility and preserve the current display mode.
    showWelcome.value = false
    await router.push(facilityHomeLink(viewer.value.homeFacility.slug))
}

async function handleWelcomeShow() {
    if (route.query.action !== 'welcome') {
        return
    }

    // Consume the invitation action only once the dialog appears. Replacing this history entry
    // prevents Back or refresh from reopening it while preserving unrelated query values and anchors.
    const queryParameters = { ...route.query }
    delete queryParameters.action
    await router.replace({
        path: route.path,
        query: queryParameters,
        hash: route.hash
    })
}

createLayout()
</script>
